import React, { useEffect } from 'react';
import { shuffle } from 'lodash';
import { checkValue, dealHelper, appendMcicid, getUniqueListBy } from 'src/components/common/helper';
// Components
import useLodgingSearchDynamic from '../../../expedia-deals/hotel/hooks/useLodgingSearchDynamic';
import {
  LodgingPwaPropertySearchQueryVariables,
  PaymentType,
  Points,
  PropertySort,
  TravelerType,
} from 'src/common/__generated__/api/types';
import { useBexApi } from '@shared-ui/bex-api-context';
import DealCardView from 'src/components/common/modules/v1/deal-fixtures/components/deal-card-view';

const DealCardByRegion: React.FC = (props: any) => {
  const { context } = useBexApi();
  const initialVariables = {
    // Set your initial variables here
    starRating: 5,
    // other variables...
  };

  const { hotelSearchData, updateVariables, fetchHotelData, isLoading } = useLodgingSearchDynamic(initialVariables);
  const { resetSearchResults, regionId, regionName, checkInDate, checkOutDate, mccidData, randomizeHotelids } = props;

  useEffect(() => {
    updateVariables(initialVariables);
    fetchHotelData();
  }, [resetSearchResults]);

  const { checkInDateFormattedInputFormat, checkOutDateFormattedInputFormat } = dealHelper.getCheckInCheckOutDates(
    checkInDate,
    checkOutDate,
  );

  let moreDealsLink = `/Hotel-Search?adults=1&destination=${props?.regionName}&endDate=${checkOutDateFormattedInputFormat.year}-${checkOutDateFormattedInputFormat.month}-${checkOutDateFormattedInputFormat.day}&paandi=true&regionId=${props?.regionId}&semdtl=&sort=RECOMMENDED&startDate=${checkInDateFormattedInputFormat.year}-${checkInDateFormattedInputFormat.month}-${checkInDateFormattedInputFormat.day}&theme=&useRewards=false&userIntent=`;
  moreDealsLink = appendMcicid(
    moreDealsLink,
    mccidData?.templateType,
    mccidData?.campaignUrl,
    mccidData?.mainUrl,
    mccidData?.referrerId,
  );

  const getHotelDataFetchProps = () => {
    const hotelDataFetchProps = {
      rId: regionId,
      rName: regionName,
      checkIn: checkInDate,
      checkOut: checkOutDate,
      sort: PropertySort.RECOMMENDED,
      priceMax: 0,
      priceMin: 10,
      adults: 1,
      starRating: props?.starRating,
      guestRating: null,
      travelerType: null,
      memberOnlyDeals: null,
      paginationSize: 4,
      paymentType: null,
    };

    const {
      rName,
      rId,
      priceMin,
      priceMax,
      adults,
      starRating,
      guestRating,
      sort,
      paginationSize,
      travelerType,
      memberOnlyDeals,
      paymentType,
    } = hotelDataFetchProps;
    const cmsStarRating = checkValue(starRating) ? parseInt(starRating?.replace(' stars', '0')) : 30;
    const starFilter = () => {
      const starRatings = [];
      for (let i = cmsStarRating; i < 60; i += 10) {
        starRatings.push(Number(i));
      }
      return starRatings;
    };
    const minGuestRating = checkValue(guestRating) ? `${guestRating}.0` : '4.0';
    const travelerTypes =
      checkValue(travelerType) && ['BUSINESS', 'FAMILY', 'LGBT', 'ROMANTIC'].includes(travelerType)
        ? [TravelerType[travelerType]]
        : null;
    // eslint-disable-next-line react/prop-types
    const memberOnlyDealsFilter =
      checkValue(memberOnlyDeals) && memberOnlyDeals?.toLowerCase() === 'true' ? ['MEMBER_ONLY'] : null;

    const paymentTypes =
      checkValue(paymentType) &&
      [
        'COUPON_ELIGIBLE',
        'FREE_CANCELLATION',
        'GIFT_CARD',
        'PAY_LATER',
        'PAY_LATER_WITH_DEPOSIT',
        'PAY_WITHOUT_CREDITCARD',
      ].includes(paymentType)
        ? [PaymentType[paymentType]]
        : null;

    const lodgingPwaPropertySearchVariables: LodgingPwaPropertySearchQueryVariables = {
      context,
      destination: {
        regionName: rName,
        regionId: rId,
        pinnedPropertyId: null,
        propertyIds: null,
        mapBounds: null,
      },
      filters: {
        starList: starFilter(),
        structureTypes: null,
        propertyName: null,
        neighborhood: rId,
        mealPlan: null,
        amenities: null,
        accessibility: null,
        travelerType: travelerTypes,
        reviewScore: {
          min: Number(`${minGuestRating}`),
          max: Number('5.0'),
        },
        poi: null,
        paymentType: paymentTypes,
        cleaningAndSafetyPractices: null,
        deals: null,
        propertyStyles: null,
        rewards: memberOnlyDealsFilter,
        bedroomFilter: [],
        price: {
          min: Number(priceMin),
          max: Number(priceMax),
        },
        commissionTiers: null,
        agencyBusinessModels: null,
        chain: null,
        stayTypes: null,
      },
      legacyCriteria: {
        sort: sort,
        stars: starFilter().join(','),
        priceBuckets: [Number(priceMin), Number(priceMax)],
        amenities: [],
        structureTypes: [],
        guestRating: `${minGuestRating}, 5.0`,
        paymentType: [],
      },
      rooms: [
        {
          adults: Number(adults),
          children: [],
        },
      ],
      propertyShopOptions: {
        points: Points.SHOP_WITH_POINTS,
      },
      shoppingContext: {
        multiItem: null,
      },
      sort: sort,
      dateRange: {
        checkInDate: checkInDateFormattedInputFormat,
        checkOutDate: checkOutDateFormattedInputFormat,
      },
      searchPagination: { size: paginationSize },
      searchIntent: {
        semdtl: null,
        theme: null,
        userIntent: null,
      },
      shouldFetchSortAndFilter: true,
      returnPropertyType: true,
    };

    return lodgingPwaPropertySearchVariables;
  };

  const handleClick = () => {
    updateVariables(getHotelDataFetchProps());
    fetchHotelData();
  };

  let searchData = checkValue(hotelSearchData)
    ? getUniqueListBy(
        hotelSearchData?.propertySearch?.propertySearchListings?.filter((el) => el?.__typename === 'Property'),
        'id',
      )
    : [];

  if (
    checkValue(randomizeHotelids) &&
    randomizeHotelids === 'True' &&
    checkValue(searchData, true) &&
    searchData.length > 2
  ) {
    searchData = shuffle(searchData).slice(0, 2);
  } else {
    searchData = searchData.slice(0, 2);
  }

  return (
    <DealCardView
      {...{
        ...props,
        searchData,
        handleClick,
        moreDealsLink,
        isLoading,
        checkInDateFormattedInputFormat,
        checkOutDateFormattedInputFormat,
      }}
    />
  );
};

export default DealCardByRegion;
